"use client";

import Icon from "@/components/icon";
import { Link, useRouter } from "@/i18n/navigation";
import { NavItem } from "@/types/blocks/base";
import { cn } from "@/lib/utils";
import { usePathname } from "next/navigation";
import { useEffect } from "react";

export default function ({
  className,
  items,
  ...props
}: {
  className?: string;
  items: NavItem[];
}) {
  const pathname = usePathname();
  const router = useRouter();

  useEffect(() => {
    if (!items?.length) return;
    if (typeof navigator === "undefined") return;

    const connection = (navigator as Navigator & {
      connection?: { saveData?: boolean; effectiveType?: string };
    }).connection;
    if (connection?.saveData) return;
    if (connection?.effectiveType === "2g" || connection?.effectiveType === "slow-2g") {
      return;
    }

    let idleId: number | null = null;
    let timeoutId: number | null = null;

    const prefetchRoutes = () => {
      items.forEach((item) => {
        if (item.url) {
          router.prefetch(item.url as any);
        }
      });
    };

    if ("requestIdleCallback" in window) {
      idleId = window.requestIdleCallback(prefetchRoutes, { timeout: 2000 });
    } else {
      timeoutId = window.setTimeout(prefetchRoutes, 1200);
    }

    return () => {
      if (idleId !== null) window.cancelIdleCallback(idleId);
      if (timeoutId !== null) window.clearTimeout(timeoutId);
    };
  }, [items, router]);

  return (
    <nav
      className={cn(
        "flex space-x-2 rounded-xl border border-white/12 bg-[linear-gradient(170deg,rgba(13,19,30,0.82),rgba(7,11,19,0.85))] p-2 backdrop-blur-sm lg:flex-col lg:space-x-0 lg:space-y-1",
        className
      )}
      {...props}
    >
      {items.map((item, index) => (
        <Link
          key={index}
          href={item.url as any}
          className={cn(
            "flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200",
            item.is_active || pathname.includes(item.url as any)
              ? "border border-primary/35 bg-primary/18 text-[#ff8f82] shadow-[0_0_0_1px_rgba(239,83,80,0.12)]"
              : "text-foreground/85 hover:bg-primary/10 hover:text-foreground"
          )}
        >
          {item.icon && <Icon name={item.icon} className="w-4 h-4" />}
          <span>{item.title}</span>
        </Link>
      ))}
    </nav>
  );
}
